/**
 * Single source of truth for an enrollment's effective date, which varies by
 * enrollmentSource (new hire vs open enrollment vs qualifying life event).
 *
 * This was inline inside Benefits.tsx's submit handler (the open-enrollment
 * branch in particular was untested). Extracted here so both enrollment surfaces
 * compute the date identically and the branches are unit-testable.
 *
 * IMPORTANT: returns `null` when a real date can't be determined. Callers MUST
 * block submission and surface an error rather than POST a made-up date — we
 * never silently default a coverage start to "today", including for open
 * enrollment: if no future plan-year boundary (plan effectiveDate or renewalDate)
 * exists, the plan is misconfigured and this returns null.
 */
import { calculateQLEEffectiveDate } from "./qleTypes";

export type EnrollmentState =
  | "new_hire"
  | "open_enrollment"
  | "qualifying_event"
  | (string & {});

export interface ResolveEffectiveDateArgs {
  enrollmentState: EnrollmentState;
  /**
   * The plan being enrolled. `effectiveDate` is the plan's own start date — the
   * schema defines it as "when the plan becomes active", so a new enrollment's
   * coverage can't begin before it. `renewalDate` / `entryDate` drive OE + QLE
   * timing.
   */
  plan?: {
    effectiveDate?: string | Date | null;
    renewalDate?: string | Date | null;
    entryDate?: string | null;
  } | null;
  /** True when the employee already completed this OE window (a modify). */
  hasCompletedOpenEnrollment?: boolean;
  /** The previous OE enrollment's effective date (ISO) — reused when modifying. */
  oeProgressKey?: string | null;
  /** The QLE event date, when enrollmentState === "qualifying_event". */
  qleEventDate?: Date | null;
  /** Precomputed QLE effective date (ISO) — used when the plan has no entry rule. */
  qleEffectiveDate?: string | null;
  /** Precomputed new-hire effective date from the eligibility endpoint (ISO). */
  newHireEffectiveDate?: string | null;
  /** Injectable "now" for deterministic tests. Defaults to new Date(). */
  now?: Date;
}

/** Returns the effective date (ISO string), or `null` when it can't be resolved. */
export function resolveEnrollmentEffectiveDate(
  args: ResolveEffectiveDateArgs,
): string | null {
  const {
    enrollmentState,
    plan,
    hasCompletedOpenEnrollment,
    oeProgressKey,
    qleEventDate,
    qleEffectiveDate,
    newHireEffectiveDate,
    now = new Date(),
  } = args;

  const raw = ((): string | null => {
    // New hire: the eligibility-endpoint date is the only valid source. No today fallback.
    if (enrollmentState === "new_hire") {
      return newHireEffectiveDate || null;
    }

    if (enrollmentState === "open_enrollment") {
      // Modifying a completed window: reuse the prior OE date.
      if (hasCompletedOpenEnrollment && oeProgressKey) return oeProgressKey;
      // Open enrollment: coverage starts on the new plan year's start date.
      // - If the picked plan is the new-year plan, use its own effectiveDate.
      // - If it's the current plan being renewed, use its renewalDate (= next
      //   year's start).
      // Do NOT apply the plan's entryDate here. entryDate is a new-hire waiting
      // period ("start the 1st of next month"); a renewal is not a new hire, so
      // every renewing plan starts on that same plan-year date.
      // If neither date is in the future the plan is set up wrong: return null so
      // the caller blocks the submit. We never fall back to today.
      if (plan) {
        const planEffective = plan.effectiveDate ? new Date(plan.effectiveDate) : null;
        const planRenewal = plan.renewalDate ? new Date(plan.renewalDate) : null;
        const baseDate =
          planEffective && planEffective > now
            ? planEffective
            : planRenewal && planRenewal > now
            ? planRenewal
            : null;
        if (!baseDate) return null;
        return baseDate.toISOString();
      }
      return null;
    }

    if (enrollmentState === "qualifying_event") {
      // QLE coverage start follows a standard rule, NOT the plan's entryDate:
      // first_of_month = event on the 1st is effective that day, otherwise the 1st
      // of the next month. (event 7/1 → 7/1 → current plan; 7/15 → 8/1 → renewal.)
      // The floor below then clamps to the plan's own start date.
      if (qleEventDate) {
        return calculateQLEEffectiveDate(qleEventDate, "first_of_month").toISOString();
      }
      // No event date: fall back to the precomputed QLE date (a real date), else null.
      return qleEffectiveDate ?? null;
    }

    return null;
  })();

  if (!raw) return null;

  // Floor: a new enrollment's coverage can't start before the plan itself is
  // active. If the computed date is earlier than the plan's effectiveDate, use
  // the plan's effectiveDate instead.
  if (plan?.effectiveDate) {
    const planStart = new Date(plan.effectiveDate);
    if (!Number.isNaN(planStart.getTime()) && new Date(raw) < planStart) {
      return planStart.toISOString();
    }
  }

  return raw;
}
